import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import copyToClipboard from 'copy-to-clipboard';

import {
  PARTNER_ADDRESS_STAGE_TENANT_REQUESTED,
  PARTNER_ADDRESS_STAGE_PARTNER_ADDED,
  PARTNER_ADDRESS_STAGE_LISTING_LEASED,
  PARTNER_ADDRESS_STAGE_LISTING_UNLISTED,
  PARTNER_ADDRESS_STAGE_LISTING_LISTED,
  PARTNER_ADDRESS_STAGE_TO_LABEL_MAPPING,
  UTILITY_TYPE_ID_INTERNET,
  PARTNER_ADDRESS_STAGE_NA,
} from 'common/utility/constants';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_YELLOW,
  SR_BUTTON_HEIGHT_SHORT,
} from 'client/components/SrButton/SrButton';

import SrLink from 'client/components/SrLink/SrLink';
import SrBox from 'client/components/SrBox';

import './UPMappingHitlistRow.less';

const getMissingInternetCell = (requiredTypes) => {
  const requiresInternet = !!requiredTypes.find(
    ({ utilityType, utilityProviderCoveragePoints }) => {
      return (
        utilityType.id === UTILITY_TYPE_ID_INTERNET && utilityProviderCoveragePoints.length === 0
      );
    },
  );
  if (!requiresInternet) {
    return '';
  }

  return 'Internet Required';
};

const getMappingsCountCell = (requiredTypes) => {
  const mappedTypes = requiredTypes.filter(
    ({ utilityProviderCoveragePoints }) => utilityProviderCoveragePoints.length > 0,
  );

  return (
    <span styleName="renterPhone">{`Mappings: ${mappedTypes.length}/${requiredTypes.length}`}</span>
  );
};

const getDateCell = (hitlistTask) => {
  let date;
  switch (hitlistTask.stage) {
    case PARTNER_ADDRESS_STAGE_TENANT_REQUESTED: {
      date = hitlistTask.tenantMissingProviderAlertedAt;
      break;
    }
    case PARTNER_ADDRESS_STAGE_LISTING_LEASED: {
      date = hitlistTask.listingLeasedAt;
      break;
    }
    case PARTNER_ADDRESS_STAGE_LISTING_UNLISTED: {
      date = hitlistTask.listingUnlistedAt;
      break;
    }
    case PARTNER_ADDRESS_STAGE_NA:
    case PARTNER_ADDRESS_STAGE_PARTNER_ADDED:
    case PARTNER_ADDRESS_STAGE_LISTING_LISTED: {
      date = hitlistTask.createdAt;
      break;
    }
  }

  if (!date) {
    return null;
  }

  const dateFormatted = moment(date).format('MMM D, h:mm A');
  return dateFormatted;
};

const getMappingConfirmedCell = (requiredTypes) => {
  // Guard against null/undefined/non-array input
  if (!requiredTypes || !Array.isArray(requiredTypes)) {
    return null;
  }

  // Check if any requiredType has mappingConfirmedAt set
  const confirmed = requiredTypes.find((rt) => rt.addressRequiredUtilityType?.mappingConfirmedAt);

  if (confirmed) {
    // Format the confirmed date in local time
    return moment(confirmed.addressRequiredUtilityType.mappingConfirmedAt)
      .local()
      .format('MMM D, h:mm A');
  }

  // Check if any requiredType needs confirmation
  const needsConfirmation = requiredTypes.some(
    (rt) => rt.addressRequiredUtilityType?.needsMappingConfirmation
  );

  if (needsConfirmation) {
    return 'Awaiting confirmation';
  }

  return null;
};

const getButtonProps = ({ hitlistTask, currentUser }) => {
  const { addressRequiredUtilityTypes } = hitlistTask;
  const { processingStartedAt, processingEndedAt } = hitlistTask.partnerAddress;

  const assignedAdmin = processingStartedAt && processingEndedAt ? null : hitlistTask.assignedAdmin;

  const mappedTypes = (addressRequiredUtilityTypes || []).filter(
    ({ utilityProviderCoveragePoints, addressRequiredUtilityType }) => {
      return (
        utilityProviderCoveragePoints.length > 0 &&
        !addressRequiredUtilityType?.needsMappingConfirmation
      );
    },
  );

  const submitEnabled = mappedTypes.length === addressRequiredUtilityTypes.length;

  if (!assignedAdmin) {
    return {
      theme: SR_BUTTON_THEME_ADOBE_GREEN,
      children: 'Start',
    };
  }

  if (assignedAdmin.id !== currentUser.id) {
    return {
      theme: SR_BUTTON_THEME_ADOBE_GREY,
      children: assignedAdmin.firstName,
      disabled: true,
    };
  }

  return {
    theme: SR_BUTTON_THEME_ADOBE_YELLOW,
    children: submitEnabled ? 'Done' : 'In Progress',
    disabled: !submitEnabled,
  };
};

const UPMappingHitlistRowHeader = ({ hitlistTask, currentUser, onButtonClick, onRowClick }) => {
  const [addressCopied, setAddressCopied] = useState(false);
  const onAddressClick = (e) => {
    e.stopPropagation();
    copyToClipboard(hitlistTask.address.display);
    setAddressCopied(true);
    setTimeout(() => {
      setAddressCopied(false);
    }, 2000);
  };

  return (
    <>
      <SrBox onClick={onRowClick} cursor={onRowClick ? 'pointer' : null} styleName="action">
        <SrBox onClick={(e) => e.stopPropagation()}>
          <SrButton
            type="button"
            heightType={SR_BUTTON_HEIGHT_SHORT}
            onClick={() => onButtonClick({ hitlistTask })}
            {...getButtonProps({
              hitlistTask,
              currentUser,
            })}
          />
        </SrBox>
      </SrBox>
      <SrBox onClick={onRowClick} cursor={onRowClick ? 'pointer' : null} styleName="description">
        <span onClick={addressCopied ? null : onAddressClick} styleName="renterName">
          {addressCopied ? 'Copied!' : hitlistTask.address.display}
        </span>
        <span styleName="internetRequired">
          {getMissingInternetCell(hitlistTask.addressRequiredUtilityTypes)}
        </span>
        {getMappingsCountCell(hitlistTask.addressRequiredUtilityTypes)}
        <span styleName="typeGroup">
          <span styleName="type">
            {PARTNER_ADDRESS_STAGE_TO_LABEL_MAPPING[hitlistTask.stage]}:
            <span styleName="date">{getDateCell(hitlistTask)}</span>
          </span>
          {(() => {
            const mappingConfirmed = getMappingConfirmedCell(hitlistTask.addressRequiredUtilityTypes);
            if (!mappingConfirmed) return null;

            if (mappingConfirmed === 'Awaiting confirmation') {
              return <span styleName="type">{mappingConfirmed}</span>;
            }

            return (
              <span styleName="type">
                Mapping Confirmed:
                <span styleName="date">{mappingConfirmed}</span>
              </span>
            );
          })()}
        </span>
      </SrBox>
      <SrLink
        to={
          `/utility-profit/${hitlistTask.utilityPartner.slug}` +
          '?backUrl=/utility-profit/admin/dashboard/mapping-hitlist'
        }
        target="_blank"
        styleName="partner"
      >
        <span styleName="listingLink">{hitlistTask.utilityPartner.name}</span>
      </SrLink>
    </>
  );
};

UPMappingHitlistRowHeader.propTypes = {
  hitlistTask: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  onButtonClick: PropTypes.func.isRequired,
};

export default UPMappingHitlistRowHeader;
